import { FieldArray, Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import type { StateMachineState, TransitionV2, V2ComparisonOperator } from '../../../ProgramStateMachine.types'
import { useStateMachineEditorContext } from '../../../context/StateMachineEditorContext'
import { V2_CONDITION_TYPES, V2_MATCH_MODES, V2_ORGANISATION_FIELDS } from '../../../utils/stateMachineV2'
import { CardRow } from '../../CardRow'

import {
  emptyCondition,
  emptyDataEvent,
  fromV2FormValues,
  toV2FormValues,
  type V2FormValues
} from './transitionV2FormValues'
import {
  Button,
  Checkbox,
  DateInput,
  Flex,
  FormField,
  Select,
  semanticColors,
  TextInput,
  Typography
} from '@percent/lemonade'

export interface TransitionEditFormV2Props {
  onSubmit: (transition: TransitionV2) => void
  onCancel?: () => void
  availableStates: StateMachineState[]
  initialValues?: TransitionV2
}

export function TransitionEditFormV2({
  onSubmit,
  onCancel,
  availableStates,
  initialValues
}: Readonly<TransitionEditFormV2Props>) {
  const { t } = useTranslation()
  const { metadata } = useStateMachineEditorContext()
  const isEditMode = !!initialValues

  const v2Meta = metadata.v2
  const conditionTypes = v2Meta?.conditionTypes ?? V2_CONDITION_TYPES
  const matchModes = v2Meta?.matchModes ?? V2_MATCH_MODES
  const orgFields = v2Meta?.organisationConditionFields ?? V2_ORGANISATION_FIELDS

  const eventNameOptions = metadata.eventNames
  const logicalOperatorOptions = metadata.logicalOperators.map(op => ({ value: op, label: op.toUpperCase() }))
  const numericOperatorOptions = metadata.comparisonOperators.map(op => ({ value: op, label: op }))
  const conditionTypeOptions = conditionTypes.map(v => ({
    value: v,
    label: t(`stateMachine.transitionEditV2.conditionType.${v}`)
  }))
  const matchModeOptions = matchModes.map(v => ({
    value: v,
    label: t(`stateMachine.transitionEditV2.matchMode.${v}`)
  }))

  const stateOptions = availableStates.map(s => ({
    value: s.id,
    label: s.metadata?.displayKey || s.id
  }))

  const validate = (values: V2FormValues): Record<string, unknown> => {
    const errors: Record<string, unknown> = {}

    if (!values.targetState) {
      errors.targetState = t('stateMachine.transitionEdit.validation.targetRequired')
    }

    const topEventsErrors = values.topEvents.map(ev =>
      ev.type.trim() === '' ? { type: t('stateMachine.transitionEditV2.validation.eventTypeRequired') } : null
    )

    if (topEventsErrors.some(e => e !== null)) {
      errors.topEvents = topEventsErrors
    }

    const dataEventsErrors = values.dataEvents.map(ev => {
      const evErrors: Record<string, unknown> = {}

      if (ev.eventType.trim() === '') {
        evErrors.eventType = t('stateMachine.transitionEditV2.validation.eventTypeRequired')
      }

      const payloadErrors = ev.payload.map(cond => {
        const cErrors: Record<string, string> = {}

        if (cond.key.trim() === '') {
          cErrors.key = t('stateMachine.transitionEditV2.validation.keyRequired')
        }

        if (cond.type === 'numeric') {
          if (cond.numericValue.trim() === '' || Number.isNaN(Number(cond.numericValue))) {
            cErrors.numericValue = t('stateMachine.transitionEditV2.validation.numericValueRequired')
          }
        } else if (cond.valuesText.trim() === '') {
          cErrors.valuesText = t('stateMachine.transitionEditV2.validation.valuesRequired')
        }

        return Object.keys(cErrors).length > 0 ? cErrors : null
      })

      if (payloadErrors.some(e => e !== null)) {
        evErrors.payload = payloadErrors
      }

      return Object.keys(evErrors).length > 0 ? evErrors : null
    })

    if (dataEventsErrors.some(e => e !== null)) {
      errors.dataEvents = dataEventsErrors
    }

    const orgConditionErrors: Record<string, { valuesText?: string }> = {}
    for (const f of orgFields) {
      const entry = values.orgConditions[f]

      if (entry.enabled && entry.valuesText.trim() === '') {
        orgConditionErrors[f] = { valuesText: t('stateMachine.transitionEditV2.validation.valuesRequired') }
      }
    }

    if (Object.keys(orgConditionErrors).length > 0) {
      errors.orgConditions = orgConditionErrors
    }

    return errors
  }

  return (
    <Formik<V2FormValues>
      initialValues={toV2FormValues(initialValues, orgFields)}
      validate={validate}
      enableReinitialize
      onSubmit={values => onSubmit(fromV2FormValues(values, orgFields))}
    >
      {({ values, errors, touched, isValid, submitCount, handleChange, handleBlur, setFieldValue, submitForm }) => (
        <Flex direction="column" flex={1} miw={0} mih={0}>
          <Flex direction="column" gap="md" flex={1} mih={0} pb={8} style={{ overflowY: 'auto' }}>
            <FormField
              label={t('stateMachine.transitionEdit.targetState')}
              status={touched.targetState && errors.targetState ? 'danger' : 'default'}
              statusMessage={touched.targetState && errors.targetState ? errors.targetState : ''}
              necessity="required"
            >
              <Select
                name="targetState"
                options={stateOptions}
                defaultValue={stateOptions.find(o => o.value === values.targetState)}
                onChange={option => setFieldValue('targetState', option?.value ?? '')}
              />
            </FormField>

            <FormField label={t('stateMachine.transitionEditV2.timeGreaterThan')}>
              <DateInput
                name="timeGreaterThan"
                value={values.timeGreaterThan !== null ? new Date(values.timeGreaterThan) : null}
                onChange={date => setFieldValue('timeGreaterThan', date ? date.getTime() : null)}
                onBlur={handleBlur}
                clearable
              />
            </FormField>

            <Flex direction="column" gap="xs">
              <Typography variant="h4">{t('stateMachine.transitionEditV2.topEventsSection')}</Typography>
              <Typography variant="small">{t('stateMachine.transitionEditV2.topEventsHelper')}</Typography>

              <FormField label={t('stateMachine.transitionEdit.logicalOperator')}>
                <Select
                  name="topLogicalOperator"
                  options={logicalOperatorOptions}
                  defaultValue={logicalOperatorOptions.find(o => o.value === values.topLogicalOperator)}
                  onChange={option => setFieldValue('topLogicalOperator', option?.value ?? 'and')}
                />
              </FormField>

              <FieldArray name="topEvents">
                {({ push: pushTop, remove: removeTop }) => (
                  <Flex direction="column" gap="xs">
                    {values.topEvents.map((ev, idx) => (
                      <CardRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={idx}
                        onRemove={() => removeTop(idx)}
                        removeLabel={t('button.remove')}
                        removeTestId={`remove-top-event-${idx}`}
                        testId={`top-event-${idx}`}
                      >
                        <FormField label={t('stateMachine.transitionEdit.eventType')}>
                          {eventNameOptions.length > 0 ? (
                            <Select
                              name={`topEvents.${idx}.type`}
                              options={eventNameOptions}
                              defaultValue={eventNameOptions.find(o => o.value === ev.type)}
                              onChange={option => setFieldValue(`topEvents.${idx}.type`, option?.value ?? '')}
                            />
                          ) : (
                            <TextInput
                              name={`topEvents.${idx}.type`}
                              value={ev.type}
                              onChange={handleChange}
                              placeholder={t('stateMachine.transitionEdit.eventNamePlaceholder')}
                            />
                          )}
                        </FormField>
                      </CardRow>
                    ))}
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => pushTop({ type: '' })}
                      data-testid="add-top-event"
                    >
                      {t('stateMachine.transitionEditV2.addTopEvent')}
                    </Button>
                  </Flex>
                )}
              </FieldArray>
            </Flex>

            <Flex direction="column" gap="xs">
              <Typography variant="h4">{t('stateMachine.transitionEditV2.orgSection')}</Typography>
              <Typography variant="small">{t('stateMachine.transitionEditV2.orgHelper')}</Typography>
              {orgFields.map(field => {
                const entry = values.orgConditions[field]

                return (
                  <CardRow key={field} testId={`org-field-${field}`}>
                    <Flex direction="column" gap="xs">
                      <Checkbox
                        name={`orgConditions.${field}.enabled`}
                        variant="default"
                        label={t(`stateMachine.transitionEditV2.orgField.${field}`)}
                        active={entry.enabled}
                        onChange={e =>
                          setFieldValue(`orgConditions.${field}.enabled`, (e.target as HTMLInputElement).checked)
                        }
                        dataTestId={`org-field-enabled-${field}`}
                      />
                      {entry.enabled && (
                        <Flex direction="column" gap="xxs" style={{ paddingLeft: 24 }}>
                          <FormField
                            label={t('stateMachine.transitionEditV2.valuesLabel')}
                            description={t('stateMachine.transitionEditV2.valuesHelper')}
                            descriptionVariant="light"
                            data-testid={`org-field-values-${field}`}
                          >
                            <TextInput
                              name={`orgConditions.${field}.valuesText`}
                              value={entry.valuesText}
                              onChange={handleChange}
                              placeholder={t('stateMachine.transitionEditV2.valuesPlaceholder')}
                            />
                          </FormField>
                          <Checkbox
                            name={`orgConditions.${field}.isOneOf`}
                            variant="default"
                            label={t('stateMachine.transitionEditV2.isOneOfLabel')}
                            active={entry.isOneOf}
                            onChange={e =>
                              setFieldValue(`orgConditions.${field}.isOneOf`, (e.target as HTMLInputElement).checked)
                            }
                            dataTestId={`org-field-isoneof-${field}`}
                          />
                        </Flex>
                      )}
                    </Flex>
                  </CardRow>
                )
              })}
            </Flex>

            <Flex direction="column" gap="xs">
              <Typography variant="h4">{t('stateMachine.transitionEditV2.dataEventsSection')}</Typography>
              <Typography variant="small">{t('stateMachine.transitionEditV2.dataEventsHelper')}</Typography>

              <FormField label={t('stateMachine.transitionEdit.logicalOperator')}>
                <Select
                  name="dataLogicalOperator"
                  options={logicalOperatorOptions}
                  defaultValue={logicalOperatorOptions.find(o => o.value === values.dataLogicalOperator)}
                  onChange={option => setFieldValue('dataLogicalOperator', option?.value ?? 'and')}
                />
              </FormField>

              <FieldArray name="dataEvents">
                {({ push: pushEvent, remove: removeEvent }) => (
                  <Flex direction="column" gap="md">
                    {values.dataEvents.map((ev, eventIndex) => {
                      const eventPayloadSuggestions = metadata.eventPayloadFields[ev.eventType] ?? []

                      return (
                        <CardRow
                          // eslint-disable-next-line react/no-array-index-key
                          key={eventIndex}
                          onRemove={() => removeEvent(eventIndex)}
                          removeLabel={t('button.remove')}
                          removeTestId={`remove-data-event-${eventIndex}`}
                          testId={`data-event-${eventIndex}`}
                        >
                          <Flex direction="column" gap="xs">
                            <FormField label={t('stateMachine.transitionEdit.eventType')}>
                              {eventNameOptions.length > 0 ? (
                                <Select
                                  name={`dataEvents.${eventIndex}.eventType`}
                                  options={eventNameOptions}
                                  defaultValue={eventNameOptions.find(o => o.value === ev.eventType)}
                                  onChange={option =>
                                    setFieldValue(`dataEvents.${eventIndex}.eventType`, option?.value ?? '')
                                  }
                                />
                              ) : (
                                <TextInput
                                  name={`dataEvents.${eventIndex}.eventType`}
                                  value={ev.eventType}
                                  onChange={handleChange}
                                  placeholder={t('stateMachine.transitionEdit.eventNamePlaceholder')}
                                />
                              )}
                            </FormField>
                            <FormField label={t('stateMachine.transitionEditV2.matchModeLabel')}>
                              <Select
                                name={`dataEvents.${eventIndex}.matchMode`}
                                options={matchModeOptions}
                                defaultValue={matchModeOptions.find(o => o.value === ev.matchMode)}
                                onChange={option =>
                                  setFieldValue(`dataEvents.${eventIndex}.matchMode`, option?.value ?? 'any')
                                }
                              />
                            </FormField>

                            <FieldArray name={`dataEvents.${eventIndex}.payload`}>
                              {({ push: pushCondition, remove: removeCondition }) => (
                                <Flex
                                  direction="column"
                                  gap="xs"
                                  style={{ paddingLeft: 12, borderLeft: `2px solid ${semanticColors.borderDefault}` }}
                                >
                                  {ev.payload.map((cond, condIndex) => (
                                    <CardRow
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={condIndex}
                                      onRemove={() => removeCondition(condIndex)}
                                      removeLabel={t('button.remove')}
                                      removeTestId={`remove-condition-${eventIndex}-${condIndex}`}
                                      testId={`data-condition-${eventIndex}-${condIndex}`}
                                    >
                                      <Flex direction="column" gap="xxs">
                                        <FormField label={t('stateMachine.transitionEditV2.conditionTypeLabel')}>
                                          <Select
                                            name={`dataEvents.${eventIndex}.payload.${condIndex}.type`}
                                            options={conditionTypeOptions}
                                            defaultValue={conditionTypeOptions.find(o => o.value === cond.type)}
                                            onChange={option =>
                                              setFieldValue(
                                                `dataEvents.${eventIndex}.payload.${condIndex}.type`,
                                                option?.value ?? 'set'
                                              )
                                            }
                                          />
                                        </FormField>
                                        <FormField
                                          label={t('stateMachine.transitionEditV2.keyLabel')}
                                          description={
                                            eventPayloadSuggestions.length > 0
                                              ? t('stateMachine.transitionEditV2.keySuggestions', {
                                                  fields: eventPayloadSuggestions.map(f => f.field).join(', ')
                                                })
                                              : t('stateMachine.transitionEditV2.keyDotPathHint')
                                          }
                                          descriptionVariant="light"
                                        >
                                          <TextInput
                                            name={`dataEvents.${eventIndex}.payload.${condIndex}.key`}
                                            value={cond.key}
                                            onChange={handleChange}
                                            placeholder={t('stateMachine.transitionEditV2.keyPlaceholder')}
                                          />
                                        </FormField>
                                        {cond.type === 'set' ? (
                                          <>
                                            <FormField
                                              label={t('stateMachine.transitionEditV2.valuesLabel')}
                                              description={t('stateMachine.transitionEditV2.valuesHelper')}
                                              descriptionVariant="light"
                                            >
                                              <TextInput
                                                name={`dataEvents.${eventIndex}.payload.${condIndex}.valuesText`}
                                                value={cond.valuesText}
                                                onChange={handleChange}
                                                placeholder={t('stateMachine.transitionEditV2.valuesPlaceholder')}
                                              />
                                            </FormField>
                                            <Checkbox
                                              name={`dataEvents.${eventIndex}.payload.${condIndex}.isOneOf`}
                                              variant="default"
                                              label={t('stateMachine.transitionEditV2.isOneOfLabel')}
                                              active={cond.isOneOf}
                                              onChange={e =>
                                                setFieldValue(
                                                  `dataEvents.${eventIndex}.payload.${condIndex}.isOneOf`,
                                                  (e.target as HTMLInputElement).checked
                                                )
                                              }
                                              dataTestId={`condition-isoneof-${eventIndex}-${condIndex}`}
                                            />
                                          </>
                                        ) : (
                                          <>
                                            <FormField label={t('stateMachine.transitionEditV2.numericOperatorLabel')}>
                                              <Select
                                                name={`dataEvents.${eventIndex}.payload.${condIndex}.numericOperator`}
                                                options={numericOperatorOptions}
                                                defaultValue={numericOperatorOptions.find(
                                                  o => o.value === cond.numericOperator
                                                )}
                                                onChange={option =>
                                                  setFieldValue(
                                                    `dataEvents.${eventIndex}.payload.${condIndex}.numericOperator`,
                                                    (option?.value ?? '==') as V2ComparisonOperator
                                                  )
                                                }
                                              />
                                            </FormField>
                                            <FormField label={t('stateMachine.transitionEditV2.numericValueLabel')}>
                                              <TextInput
                                                name={`dataEvents.${eventIndex}.payload.${condIndex}.numericValue`}
                                                value={cond.numericValue}
                                                onChange={handleChange}
                                                placeholder={t('stateMachine.transitionEditV2.numericValuePlaceholder')}
                                              />
                                            </FormField>
                                          </>
                                        )}
                                      </Flex>
                                    </CardRow>
                                  ))}
                                  <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => pushCondition(emptyCondition())}
                                    data-testid={`add-condition-${eventIndex}`}
                                  >
                                    {t('stateMachine.transitionEditV2.addCondition')}
                                  </Button>
                                </Flex>
                              )}
                            </FieldArray>
                          </Flex>
                        </CardRow>
                      )
                    })}
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => pushEvent(emptyDataEvent())}
                      data-testid="add-data-event"
                    >
                      {t('stateMachine.transitionEditV2.addDataEvent')}
                    </Button>
                  </Flex>
                )}
              </FieldArray>
            </Flex>
          </Flex>

          <Flex
            direction="column"
            gap="xs"
            pt={12}
            style={{ flexShrink: 0, borderTop: `1px solid ${semanticColors.borderDefault}` }}
            data-testid="transition-edit-footer"
          >
            {submitCount > 0 && !isValid && (
              <Typography
                variant="small"
                style={{ color: semanticColors.contentError }}
                data-testid="transition-edit-validation-summary"
              >
                {t('stateMachine.transitionEditV2.validation.completeAllFields')}
              </Typography>
            )}
            <Flex gap="xs" justify="flex-end">
              {onCancel && (
                <Button variant="secondary" onClick={onCancel} data-testid="transition-edit-cancel">
                  {t('button.cancel')}
                </Button>
              )}
              <Button variant="primary" onClick={submitForm} data-testid="transition-edit-submit">
                {isEditMode ? t('button.save') : t('stateMachine.transitionEdit.addTransition')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Formik>
  )
}
